/**
 * Onboarding séquentiel — détermine quelles pages sont accessibles à l'utilisateur
 * selon l'avancement de sa configuration.
 *
 * Pourquoi ce hook ?
 * ──────────────────
 * Avant ce hook, toutes les pages étaient accessibles dès le signup. Un utilisateur
 * qui ne savait pas par où commencer pouvait tourner en rond entre Sources / Canaux
 * sans avoir défini son Positionnement, puis essayer de générer des posts en l'absence
 * de tout. Résultat : confusion + tickets support.
 *
 * On force désormais un ordre :
 *   1. Positionnement (toujours dispo)
 *   2. Centres d'intérêt (Watch) — débloqué quand le positionnement est défini
 *   3. Sources — débloqué quand au moins 1 centre d'intérêt existe
 *   4. Canaux de communication — débloqué quand au moins 1 source existe
 *   5. Galerie publications / création de posts — débloqué quand au moins 1 canal existe
 *
 * Les onglets `Automatisations` et `Newsletter` restent verrouillés en permanence
 * (badge "Bientôt disponible" — features non-implémentées en prod aujourd'hui).
 *
 * ⚠️ Important : ce hook NE S'APPLIQUE PAS aux utilisateurs en mode lecture seule
 * (membres d'agence avec rôle `viewer`). Pour eux, tout est déjà déblocé en lecture,
 * et les boutons d'action sont gérés par `useReadOnly()`. On ne veut surtout pas que
 * les deux mécanismes se chevauchent (ex: cacher un onglet à un viewer juste parce que
 * le space n'a pas de sources définies). Quand `isReadOnly=true`, on retourne tout
 * unlocked sauf le coming-soon.
 *
 * Source de vérité : `useSetupWizard()` qui calcule les 4 étapes à partir des données
 * actuellement chargées (positioning, watchTopics, sources, channels). Pas de
 * fetch supplémentaire — on consomme l'existant.
 */
import { useMemo } from 'react';
import { useSetupWizard, SetupWizardData } from './useSetupWizard';
import { useReadOnly } from './useReadOnly';

/**
 * Étapes de l'onboarding séquentiel.
 *
 * `currentStep` désigne la **prochaine étape à compléter** : si l'utilisateur
 * n'a pas encore défini son positionnement, c'est `POSITIONING` ; quand tout
 * est OK, on est sur `COMPLETE`.
 */
export enum OnboardingStep {
  POSITIONING = 'positioning',
  TOPICS = 'topics',
  SOURCES = 'sources',
  CHANNELS = 'channels',
  COMPLETE = 'complete',
}

export interface OnboardingUnlocked {
  positioning: boolean; // toujours true
  topics: boolean;
  sources: boolean;
  channels: boolean;
  posts: boolean;
  // V4-SIDEBAR-LOCK-NO-POST 2026-05-28 — requièrent au moins 1 post existant
  // (snapshot/comment/queue n'ont aucun sens avant la première génération).
  analytics: boolean;
  engagement: boolean;
  publications: boolean;
  automations: false; // coming soon
  newsletter: false;  // coming soon
}

export interface OnboardingProgress {
  /** Prochaine étape à compléter, ou `complete` si tout est fait. */
  currentStep: OnboardingStep;
  /** Map des sections accessibles. */
  unlocked: OnboardingUnlocked;
  /** Vrai si les 4 étapes principales sont OK. */
  isOnboardingComplete: boolean;
  /** Vrai si l'utilisateur est en lecture seule (le hook bypass alors les locks). */
  isReadOnly: boolean;
}

/**
 * Pour un utilisateur en lecture seule, on retourne tout débloqué (sauf coming-soon).
 * Pour les autres, on calcule le déblocage en cascade.
 */
export function useOnboardingProgress(data: SetupWizardData): OnboardingProgress {
  const isReadOnly = useReadOnly();
  const { steps, isComplete } = useSetupWizard(data);

  return useMemo<OnboardingProgress>(() => {
    if (isReadOnly) {
      // En lecture seule, tout est unlocked sauf coming-soon. Pour Analytics /
      // Engagement / Publications on garde aussi unlocked car le reader pourrait
      // déjà avoir des posts publiés (cas agency_viewer sur space avec contenu).
      return {
        currentStep: OnboardingStep.COMPLETE,
        unlocked: {
          positioning: true,
          topics: true,
          sources: true,
          channels: true,
          posts: true,
          analytics: true,
          engagement: true,
          publications: true,
          automations: false,
          newsletter: false,
        },
        isOnboardingComplete: true,
        isReadOnly: true,
      };
    }

    const hasPositioning = steps.hasPositioning;
    // V4-ONBOARDING-CHECKLIST-OPTIONAL — topics/sources « satisfaits » soit parce
    // qu'ils existent, soit parce que la veille est désactivée (personal branding).
    // La cascade de déblocage (canaux/posts) ne les exige plus dans ce cas.
    const hasTopic = steps.topicsSatisfied;
    const hasSource = steps.sourcesSatisfied;
    const hasChannel = steps.hasChannel;
    const hasPost = steps.hasPost;

    let currentStep: OnboardingStep = OnboardingStep.COMPLETE;
    if (!hasPositioning) currentStep = OnboardingStep.POSITIONING;
    else if (!hasTopic) currentStep = OnboardingStep.TOPICS;
    else if (!hasSource) currentStep = OnboardingStep.SOURCES;
    else if (!hasChannel) currentStep = OnboardingStep.CHANNELS;

    // V4-SIDEBAR-LOCK-NO-POST 2026-05-28 — Analytics / Engagement / Publications
    // exigent au moins 1 post (sinon page vide / non pertinente).
    const postPagesUnlocked = hasPositioning && hasTopic && hasSource && hasChannel && hasPost;

    return {
      currentStep,
      unlocked: {
        positioning: true,
        topics: hasPositioning,
        sources: hasPositioning && hasTopic,
        channels: hasPositioning && hasTopic && hasSource,
        posts: hasPositioning && hasTopic && hasSource && hasChannel,
        analytics: postPagesUnlocked,
        engagement: postPagesUnlocked,
        publications: postPagesUnlocked,
        automations: false,
        newsletter: false,
      },
      isOnboardingComplete: isComplete,
      isReadOnly: false,
    };
  }, [isReadOnly, steps.hasPositioning, steps.topicsSatisfied, steps.sourcesSatisfied, steps.hasChannel, steps.hasPost, isComplete]);
}
